import {
  Box,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import { FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import type { ProfileComparison, FunctionDiff, Profile } from '@/lib/sdk/profiling';

interface ProfileCompareProps {
  comparison?: ProfileComparison;
  isLoading?: boolean;
}

interface ProfileSummaryCardProps {
  profile: Profile;
  label: string;
}

function ProfileSummaryCard({ profile, label }: ProfileSummaryCardProps) {
  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${bytes} B`;
  };

  return (
    <Card variant="outline" bg="bg-surface" bgGradient={cardGradient}>
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Badge colorScheme={label === 'Base' ? 'blue' : 'green'} variant="solid">
            {label}
          </Badge>
          <Text color="text-muted" fontSize="xs">
            {new Date(profile.created_at).toLocaleDateString()}
          </Text>
        </HStack>
      </CardHeader>
      <CardBody pt={2}>
        <VStack align="start" spacing={2}>
          <HStack>
            <Text color="text-secondary" fontSize="sm">Type:</Text>
            <Badge colorScheme="purple">{profile.profile_type.toUpperCase()}</Badge>
          </HStack>
          <HStack>
            <Text color="text-secondary" fontSize="sm">Size:</Text>
            <Text color="text-primary" fontSize="sm">{formatBytes(profile.file_size_bytes)}</Text>
          </HStack>
          <HStack>
            <Text color="text-secondary" fontSize="sm">Duration:</Text>
            <Text color="text-primary" fontSize="sm">{profile.duration_seconds}s</Text>
          </HStack>
          {profile.description && (
            <Text color="text-muted" fontSize="xs" noOfLines={2}>
              {profile.description}
            </Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}

interface DiffRowProps {
  diff: FunctionDiff;
}

function DiffRow({ diff }: DiffRowProps) {
  const rowGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const getTrendIcon = () => {
    switch (diff.trend) {
      case 'improved':
        return <Icon as={FiArrowDown} color="green.500" />;
      case 'degraded':
        return <Icon as={FiArrowUp} color="red.500" />;
      default:
        return <Icon as={FiMinus} color="gray.500" />;
    }
  };

  const getTrendColor = () => {
    switch (diff.trend) {
      case 'improved':
        return 'green';
      case 'degraded':
        return 'red';
      default:
        return 'gray';
    }
  };

  const diffPercent = Math.abs(diff.diff_percent);

  return (
    <Tr bgGradient={rowGradient}>
      <Td borderColor="border-default">
        <Text
          color="text-primary"
          fontSize="sm"
          fontFamily="mono"
          maxW="300px"
          isTruncated
        >
          {diff.function_name.split('/').pop() || diff.function_name}
        </Text>
      </Td>
      <Td borderColor="border-default" isNumeric>
        <Text color="text-secondary" fontSize="sm" fontFamily="mono">
          {diff.base_value.toLocaleString()}
        </Text>
      </Td>
      <Td borderColor="border-default" isNumeric>
        <Text color="text-secondary" fontSize="sm" fontFamily="mono">
          {diff.compare_value.toLocaleString()}
        </Text>
      </Td>
      <Td borderColor="border-default">
        <HStack justify="flex-end">
          {getTrendIcon()}
          <Badge
            colorScheme={getTrendColor()}
            variant="subtle"
          >
            {diff.trend === 'improved' ? '-' : diff.trend === 'degraded' ? '+' : ''}
            {diffPercent.toFixed(1)}%
          </Badge>
        </HStack>
      </Td>
      <Td borderColor="border-default" w="150px">
        <Progress
          value={Math.min(diffPercent, 100)}
          size="sm"
          colorScheme={getTrendColor()}
          borderRadius="full"
        />
      </Td>
    </Tr>
  );
}

export function ProfileCompare({ comparison, isLoading }: ProfileCompareProps) {
  if (isLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Skeleton height="150px" borderRadius="lg" />
          <Skeleton height="150px" borderRadius="lg" />
        </SimpleGrid>
        <Skeleton height="300px" borderRadius="lg" />
      </VStack>
    );
  }

  if (!comparison) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="text-muted">
          Select two profiles to compare their performance
        </Text>
      </Box>
    );
  }

  const improvedCount = comparison.diff_functions.filter(d => d.trend === 'improved').length;
  const degradedCount = comparison.diff_functions.filter(d => d.trend === 'degraded').length;

  return (
    <VStack spacing={6} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <ProfileSummaryCard profile={comparison.base_profile} label="Base" />
        <ProfileSummaryCard profile={comparison.compare_profile} label="Compare" />
      </SimpleGrid>

      <Card variant="outline" bg="bg-surface">
        <CardHeader>
          <HStack justify="space-between" wrap="wrap" gap={2}>
            <Heading size="sm" color="text-primary">Function Differences</Heading>
            <HStack spacing={2}>
              <Badge colorScheme="green" variant="solid">
                {improvedCount} Improved
              </Badge>
              <Badge colorScheme="red" variant="solid">
                {degradedCount} Degraded
              </Badge>
            </HStack>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          {comparison.summary && (
            <Box
              p={4}
              mb={4}
              bg="bg-muted"
              borderRadius="md"
            >
              <Text color="text-secondary" fontSize="sm">
                {comparison.summary}
              </Text>
            </Box>
          )}

          {comparison.diff_functions.length === 0 ? (
            <Text color="text-muted" textAlign="center" py={4}>
              No significant differences found between profiles
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg="bg-muted">
                  <Tr>
                    <Th color="text-primary" borderColor="border-default">Function</Th>
                    <Th color="text-primary" borderColor="border-default" isNumeric>Base</Th>
                    <Th color="text-primary" borderColor="border-default" isNumeric>Compare</Th>
                    <Th color="text-primary" borderColor="border-default" isNumeric>Change</Th>
                    <Th color="text-primary" borderColor="border-default" w="150px">Impact</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {comparison.diff_functions.map((diff, index) => (
                    <DiffRow key={index} diff={diff} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
}
